import { forwardRef } from "react";

import { Text } from "chameleon/lib/components";

import {
  ecpPaymentClass,
  formCurrentAddressClass,
  noPadding,
} from "checkout-experience/components/forms/form-current-address.css";
import { validateCountryCode } from "checkout-experience/utils/form-field-helpers";
import { ShippingAddress } from "checkout-query-client/hooks/use-checkout-session";
import { PaymentType } from "checkout-query-client/types/form-types";

import { PostalAddress } from "frontend-client";

interface BillingAddressCollection {
  collectAccountHolderName: boolean;
  collectAddressCityState: boolean;
  collectAddressLine2: boolean;
  collectCardholderName: boolean;
  collectCountryAndZip: boolean;
  enabled: boolean;
  showBillingSameAsShippingCheckbox: boolean;
}
export interface CurrentShippingAddress {
  paymentType: PaymentType;
  shippingAddress?: ShippingAddress;
  billingAddress?: Partial<PostalAddress>;
  billingAddressCollection?: BillingAddressCollection;
}

interface Props {
  currentShippingAddress?: CurrentShippingAddress;
  withEdit?: boolean;
  value?: string;
}

export const FormCurrentAddress = forwardRef<HTMLDivElement, Props>(
  ({ currentShippingAddress, withEdit }: Props, ref) => {
    if (!currentShippingAddress) return <></>;

    const {
      paymentType,
      shippingAddress,
      billingAddress,
      billingAddressCollection,
    } = currentShippingAddress;

    const billingAddressFromInput: string[] = [];
    if (
      billingAddress?.addressLines?.length &&
      billingAddressCollection?.collectAddressCityState
    ) {
      billingAddressFromInput.push(billingAddress?.addressLines[0]);
      if (
        billingAddress?.addressLines[1].length > 0 &&
        billingAddressCollection?.collectAddressLine2
      ) {
        billingAddressFromInput.push(billingAddress?.addressLines[1]);
      }
    }
    if (
      billingAddressCollection?.collectAddressCityState &&
      billingAddress?.locality
    ) {
      billingAddressFromInput.push(billingAddress?.locality);
    }
    if (
      billingAddressCollection?.collectAddressCityState &&
      billingAddress?.administrativeArea
    ) {
      billingAddressFromInput.push(billingAddress?.administrativeArea);
    }
    if (
      billingAddressCollection?.collectCountryAndZip &&
      billingAddress?.postalCode
    ) {
      billingAddressFromInput.push(billingAddress?.postalCode);
    }
    if (
      billingAddressCollection?.collectCountryAndZip &&
      billingAddress?.regionCode
    ) {
      billingAddressFromInput.push(
        validateCountryCode(billingAddress?.regionCode)
      );
    }

    const billingAddressFromShipping: string[] = [];
    if (paymentType === PaymentType.eCheck && shippingAddress?.recipientName) {
      billingAddressFromShipping.push(shippingAddress?.recipientName);
    }
    if (shippingAddress?.line1) {
      billingAddressFromShipping.push(shippingAddress?.line1);
    }
    if (
      billingAddressCollection?.collectAddressLine2 &&
      shippingAddress?.line2
    ) {
      billingAddressFromShipping.push(shippingAddress?.line2);
    }
    if (
      billingAddressCollection?.collectAddressCityState &&
      shippingAddress?.locality
    ) {
      billingAddressFromShipping.push(shippingAddress?.locality);
    }
    if (
      billingAddressCollection?.collectAddressCityState &&
      shippingAddress?.administrativeArea
    ) {
      billingAddressFromShipping.push(shippingAddress?.administrativeArea);
    }
    if (
      billingAddressCollection?.collectCountryAndZip &&
      shippingAddress?.postalCode
    ) {
      billingAddressFromShipping.push(shippingAddress?.postalCode);
    }
    if (
      billingAddressCollection?.collectCountryAndZip &&
      shippingAddress?.regionCode
    ) {
      billingAddressFromShipping.push(
        validateCountryCode(shippingAddress?.regionCode)
      );
    }

    return (
      <div
        className={`${formCurrentAddressClass} ${withEdit ? noPadding : ""}`}
        ref={ref}
      >
        <div
          className={paymentType === PaymentType.eCheck ? ecpPaymentClass : ""}
        >
          {billingAddress?.postalCode ? (
            <Text styleVariant="body">
              {billingAddressFromInput.join(", ")}
            </Text>
          ) : null}
          {!billingAddress?.postalCode &&
          shippingAddress &&
          billingAddressCollection?.showBillingSameAsShippingCheckbox ? (
            <Text styleVariant="body">
              {billingAddressFromShipping.join(", ")}
            </Text>
          ) : null}
        </div>
      </div>
    );
  }
);

FormCurrentAddress.displayName = "FormCurrentAddress";
